// Stale Listings Report — 4mo / 8mo / 12mo tiers with actions
import React, { useState, useEffect } from 'react';
import { api } from '../../App';

const TIERS = [
  { key: 'months_12', label: '12+ Months', color: 'var(--danger)',  action: 'End & Relist',                  hint: 'End the listing and relist fresh to reset Cassini ranking' },
  { key: 'months_8',  label: '8–12 Months', color: 'var(--warn)',   action: 'Review Title, Description & Price', hint: 'Refresh the copy and reprice to stay competitive' },
  { key: 'months_4',  label: '4–8 Months',  color: '#f4a261',       action: 'Check Pricing',                 hint: 'Compare current price against market and adjust if needed' },
];

export default function StaleListings() {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(false);
  const [selected, setSelected] = useState({});
  const [working, setWorking]   = useState(false);
  const [results, setResults]   = useState(null);

  function load() {
    setLoading(true);
    setResults(null);
    api.get('/reports/stale-listings').then(r => { setData(r.data); setLoading(false); });
  }

  function toggleSelect(sku) {
    setSelected(prev => ({ ...prev, [sku]: !prev[sku] }));
  }

  function selectAll(tierKey) {
    const items = data?.tiers?.[tierKey] || [];
    const allSelected = items.every(i => selected[i.sku]);
    const next = { ...selected };
    items.forEach(i => { next[i.sku] = !allSelected; });
    setSelected(next);
  }

  async function endAndRelist() {
    const skus = Object.entries(selected).filter(([,v]) => v).map(([k]) => k);
    if (!skus.length) return;
    setWorking(true);
    const r = await api.post('/reports/stale-listings/end-relist', { skus }).catch(e => e.response);
    setResults(r?.data);
    setWorking(false);
    setSelected({});
    load();
  }

  const selectedCount = Object.values(selected).filter(Boolean).length;

  if (!data && !loading) return (
    <div className="empty-state">
      <div className="empty-icon">⏳</div>
      <button className="btn btn-primary" onClick={load}>Run Stale Listing Report</button>
    </div>
  );

  if (loading) return <div className="loading">Analyzing listings…</div>;

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <div style={{ fontSize:12, color:'var(--muted)' }}>
          Generated: {data.generated_at ? new Date(data.generated_at).toLocaleString() : ''}
        </div>
        <div style={{ display:'flex', gap:8 }}>
          {selectedCount > 0 && (
            <button className="btn btn-danger" onClick={endAndRelist} disabled={working}>
              {working ? '⏳ Processing…' : `🔁 End & Relist (${selectedCount})`}
            </button>
          )}
          <button className="btn btn-ghost" onClick={load}>🔄 Refresh</button>
        </div>
      </div>

      {/* Summary */}
      <div className="stats-grid" style={{ marginBottom:20 }}>
        {TIERS.map(t => (
          <div key={t.key} className="stat-card">
            <div className="stat-value" style={{ color: t.color }}>{data.summary[t.key.replace('months_','tier_')]}</div>
            <div className="stat-label">{t.label}</div>
          </div>
        ))}
        <div className="stat-card">
          <div className="stat-value">{data.summary.total}</div>
          <div className="stat-label">Total Stale</div>
        </div>
      </div>

      {results && (
        <div className="card" style={{ marginBottom:16, borderColor: results.failed?.length ? 'var(--danger)' : 'var(--accent2)' }}>
          <span className="success-text">✓ {results.success?.length} relisted</span>
          {results.failed?.length > 0 && <span className="error-text" style={{ marginLeft:12 }}>✗ {results.failed.length} failed</span>}
        </div>
      )}

      {TIERS.map(tier => {
        const items = data.tiers[tier.key] || [];
        if (!items.length) return null;
        const allSelected = items.every(i => selected[i.sku]);

        return (
          <div key={tier.key} style={{ marginBottom:24 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
              <div>
                <span style={{ fontWeight:700, fontSize:15, color: tier.color }}>{tier.label}</span>
                <span style={{ fontSize:12, color:'var(--muted)', marginLeft:10 }}>{items.length} listings · {tier.hint}</span>
              </div>
              {tier.key === 'months_12' && (
                <button className="btn btn-ghost" style={{ fontSize:12 }} onClick={() => selectAll(tier.key)}>
                  {allSelected ? 'Deselect All' : 'Select All'}
                </button>
              )}
            </div>

            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    {tier.key === 'months_12' && <th style={{ width:32 }}></th>}
                    <th>SKU</th><th>Title</th><th>Location</th><th>Qty</th><th>Age</th><th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(item => (
                    <tr key={item.sku} style={{ opacity: selected[item.sku] ? 1 : .85 }}>
                      {tier.key === 'months_12' && (
                        <td>
                          <input type="checkbox" checked={!!selected[item.sku]} onChange={() => toggleSelect(item.sku)}
                            style={{ width:'auto', accentColor:'var(--accent)' }} />
                        </td>
                      )}
                      <td className="mono" style={{ fontSize:11 }}>{item.sku}</td>
                      <td style={{ maxWidth:220, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontSize:12 }}>{item.title}</td>
                      <td><span style={{ fontFamily:'monospace', fontSize:11, color:'var(--accent)' }}>{item.location || '—'}</span></td>
                      <td style={{ color: item.quantity === 0 ? 'var(--danger)' : 'var(--text)' }}>{item.quantity}</td>
                      <td style={{ color: tier.color, fontWeight:600 }}>{item.age_months}mo</td>
                      <td><span className="badge" style={{ background:`rgba(255,255,255,.06)`, color: tier.color, border:`1px solid ${tier.color}40` }}>{tier.action}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}
